import { REJECT, array, bool, nullable, num, object, optional, str, type FieldParser, type Received } from 'validation';

import { getRecord } from 'api/shared/getRecord';

// Source of truth for the wire shape: nap-backend `routes/workflows.js` GET /workflows/:workflow_id, with each action's
// `dropdown_parameters` attached by `getWorkflowActions` from `node_instance_parameters`. The route also returns the
// workflow's parameters, stages and current stage, and per action its files, change details and upload flag; the inbox
// detail panel reads none of those. This projection declares what the panel renders and what it sends back when the
// operator responds or saves, so the panel can refuse a record it cannot act on without being refused over the rest.
// `can_confirm` says whether the caller may answer every open action (nap-backend#2009). The routes check each action
// on its own, in the zone of the node instance waiting for it (its `confirm_zone_id` / `confirm_zone_name`,
// nap-backend#1951), which differs from the message's zone for an action added from another zone's queue; each action's
// own `can_confirm` is that check, so the panel offers the answers and inputs per action. The zone, queue and definition
// name say where the message's node instance ran (nap-backend#2011), all null for a message without a node instance.
//
// The text columns are nullable in `workflows` / `workflow_actions` and are copied from node configuration, so they are
// declared nullable. The timestamps are nullable columns too, but default to now() and are never written explicitly.

/** An answer the operator can give: a node definition handle, so `id` is a uuid and the thing `respond` sends back. */
export interface InboxWorkflowResponseOption {
  readonly id: string;
  readonly label: string;
}

/** A free-text field the operator fills in before responding. */
export interface InboxWorkflowInputOption {
  readonly label: string;
}

/** A saved value of one of those fields. */
export interface InboxWorkflowInput {
  readonly label: string;
  readonly value: string;
}

/**
 * The answer given. Written by `PUT /workflows/respond/:id` as the chosen option plus `user` and `timestamp`, and by a
 * cancelled node instance as a bare label with a nullable user.
 */
export interface InboxWorkflowResponse {
  readonly label: string;
  readonly user: string | null;
  readonly timestamp: number;
}

/** A `Dropdown` / `KeyValueDropdown` parameter of the action's node instance, which the operator may change. */
export interface InboxWorkflowDropdownParameter {
  readonly parameter_id: number;
  readonly name: string;
  readonly default_expression: string | null;
  readonly value: string | null;
  readonly submit_value: string | null;
  readonly out_value: string | null;
  /** Comma-separated choices. */
  readonly options: string | null;
}

export interface InboxWorkflowAction {
  readonly workflow_action_id: number;
  readonly subject: string | null;
  readonly message: string | null;
  readonly created_at: string;
  readonly response: InboxWorkflowResponse | null;
  readonly response_options: readonly InboxWorkflowResponseOption[];
  readonly response_input_options: readonly InboxWorkflowInputOption[];
  readonly response_inputs: readonly InboxWorkflowInput[];
  readonly dropdown_parameters: readonly InboxWorkflowDropdownParameter[];
  // Optional: a backend before nap-backend#1951 sends none, and the message's `can_confirm` stands in.
  readonly confirm_zone_id?: number | null;
  readonly confirm_zone_name?: string | null;
  readonly can_confirm?: boolean;
}

export interface InboxWorkflow {
  readonly workflow_id: number;
  readonly workflow_subject: string | null;
  readonly workflow_category: string | null;
  readonly created_at: string;
  readonly updated_at: string;
  // Display only, so optional: a backend without them (before nap-backend#2011) still opens the message.
  readonly zone_id?: number | null;
  readonly zone_name?: string | null;
  readonly queue_name?: string | null;
  readonly definition_name?: string | null;
  readonly can_confirm: boolean;
  readonly workflows_actions: readonly InboxWorkflowAction[];
}

const inputSchema = object<InboxWorkflowInput>({ label: str(), value: str() });

const isEmptyObject = (raw: unknown): boolean => typeof raw === 'object' && raw !== null && !Array.isArray(raw) && Object.keys(raw).length === 0;

/**
 * NOTE: an action whose inputs were never saved has a NULL column, which the route sends as `{}` (`response_inputs ||
 * {}`). That is the wire's spelling of "none", not drift, so it reads as an empty list; any other non-array still drifts.
 */
const inputsSchema: FieldParser<InboxWorkflowInput[]> = (raw, path, warnings) =>
  isEmptyObject(raw) ? { ok: true, value: [] } : array(inputSchema)(raw, path, warnings);

const actionSchema = object<InboxWorkflowAction>({
  workflow_action_id: num(REJECT),
  subject: nullable(str()),
  message: nullable(str()),
  created_at: str(),
  response: nullable(object<InboxWorkflowResponse>({ label: str(), user: nullable(str()), timestamp: num() })),
  response_options: array(object<InboxWorkflowResponseOption>({ id: str(REJECT), label: str() })),
  response_input_options: array(object<InboxWorkflowInputOption>({ label: str() })),
  response_inputs: inputsSchema,
  dropdown_parameters: array(
    object<InboxWorkflowDropdownParameter>({
      parameter_id: num(REJECT),
      name: str(),
      default_expression: nullable(str()),
      value: nullable(str()),
      submit_value: nullable(str()),
      out_value: nullable(str()),
      options: nullable(str()),
    })
  ),
  confirm_zone_id: optional(nullable(num())),
  confirm_zone_name: optional(nullable(str())),
  can_confirm: optional(bool()),
});

const workflowSchema = object<InboxWorkflow>({
  workflow_id: num(REJECT),
  workflow_subject: nullable(str()),
  workflow_category: nullable(str()),
  created_at: str(),
  updated_at: str(),
  zone_id: optional(nullable(num())),
  zone_name: optional(nullable(str())),
  queue_name: optional(nullable(str())),
  definition_name: optional(nullable(str())),
  // Deliberately refused when missing or not a boolean: guessing either way is wrong. `true` offers answers the server
  // refuses; `false` greys out a message the operator may well answer. The panel shows the load error instead, so a
  // backend without nap-backend#2009 is noticed rather than silently locking every message.
  can_confirm: bool(REJECT),
  workflows_actions: array(actionSchema),
});

/**
 * GET `/workflows/:workflow_id` for the inbox detail panel, which reads its workflow by id rather than out of the paged
 * sidebar list, so a deep link to a workflow beyond the loaded pages still shows. See `getRecord` for the outcomes:
 * `null` is the server saying there is no such workflow.
 */
export const getInboxWorkflow = (id: number): Promise<Received<InboxWorkflow | null>> =>
  getRecord(`/workflows/${id}`, { schema: workflowSchema, source: 'getInboxWorkflow' });
